import React, { useState, useEffect } from 'react';
import { useLoaderData, useSearchParams } from "react-router-dom";
import LoadingScreen from './LoadingScreen';
import { createDollarFormatter } from './TableUtils.js';
import './TableUtils.css';
import './Spending.css';

import 'react-tabulator/lib/styles.css';
import 'react-tabulator/lib/css/tabulator.min.css';
import { ReactTabulator } from 'react-tabulator';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const indentOf = (str) => {
  var result = 0;
  for (var i = 0; i < str.length; i++) {
    if (str.charAt(i) === ' ') {
      result++;
    } else {
      break;
    }
  }
  return result;
};

const treeify = (flat) => {
  var lastOfIndent = {};
  var result = [];
  flat.forEach((row) => {
    const indent = indentOf(row['spending_category']);
    if (indent === 0) {
      result.push(row);
    } else {
      const parent = lastOfIndent[indent - 4];
      if (parent) {
        if (!parent['_children']) {
          parent['_children'] = [];
        }
        parent['_children'].push(row);
      } else {
        // Orphan — add at root level
        result.push(row);
      }
    }
    lastOfIndent[indent] = row;
  });
  return result;
};

// Pivot flat multi-period rows into tree rows with per-period values
const pivotAndTreeify = (flat, periods) => {
  const byCat = {};
  const catOrder = [];
  flat.forEach((row) => {
    const cat = row.spending_category;
    if (!byCat[cat]) {
      byCat[cat] = {};
      catOrder.push(cat);
    }
    byCat[cat][row.report_period] = parseFloat(row.value_usd) || 0;
  });

  // Deduplicate while preserving first-seen order
  const seen = new Set();
  const uniqueOrder = [];
  catOrder.forEach((cat) => {
    if (!seen.has(cat)) {
      seen.add(cat);
      uniqueOrder.push(cat);
    }
  });

  const pivoted = uniqueOrder.map((cat) => {
    const row = { spending_category: cat };
    let total = 0;
    periods.forEach((p) => {
      const val = byCat[cat][p] || 0;
      row['period_' + p] = val;
      total += val;
    });
    if (periods.length > 1) {
      row['period_total'] = total;
    }
    return row;
  });

  return treeify(pivoted);
};

const getPeriodsForGranularity = (year, granularity) => {
  if (granularity === 'quarterly') {
    return [year + '-Q1', year + '-Q2', year + '-Q3', year + '-Q4'];
  } else if (granularity === 'monthly') {
    return Array.from({ length: 12 }, (_, i) => year + '-' + String(i + 1).padStart(2, '0'));
  }
  return [year];
};

const periodLabel = (period) => {
  if (/^\d{4}-Q\d$/.test(period)) return period.slice(5);
  if (/^\d{4}-\d{2}$/.test(period)) {
    return MONTH_LABELS[parseInt(period.slice(5), 10) - 1] || period;
  }
  return '$';
};

// Cache promises (not resolved values) to avoid duplicate in-flight requests
let _periodsPromise = null;
const getPeriods = () => {
  if (_periodsPromise) return _periodsPromise;
  const url = (process.env.NODE_ENV === 'development' ? '/expense_periods.json' : '/expense_periods');
  _periodsPromise = fetch(url).then((res) => res.json());
  return _periodsPromise;
};

const _expensesCache = new Map();
const getExpenses = (year, granularity) => {
  const key = year + '|' + granularity;
  if (_expensesCache.has(key)) return _expensesCache.get(key);
  const promise = (async () => {
    let url;
    if (process.env.NODE_ENV === 'development') {
      url = '/expenses.json';
    } else {
      url = '/expenses?year=' + year + '&granularity=' + granularity;
    }
    const raw = await fetch(url).then((res) => res.json());
    const periods = getPeriodsForGranularity(year, granularity);
    const filtered = process.env.NODE_ENV === 'development'
      ? raw.filter((r) => periods.includes(r.report_period))
      : raw;
    return pivotAndTreeify(filtered, periods);
  })();
  _expensesCache.set(key, promise);
  return promise;
};

const DEFAULT_YEAR = String(new Date().getFullYear() - 1);

export const loader = async ({ request }) => {
  const url = new URL(request.url);
  const year = url.searchParams.get('year') || DEFAULT_YEAR;
  const granularity = url.searchParams.get('granularity') || 'annual';

  // Fire both fetches in parallel — results are cached as promises
  getPeriods();
  getExpenses(year, granularity);

  return { initialYear: year, initialGranularity: granularity };
};

function categoryLinkFormatter(year) {
  return (cell) => {
    const value = cell.getValue();
    if (value == null || value === '') return '';
    const trimmed = value.trim();
    const indent = indentOf(value);
    const content = document.createElement("a");
    content.textContent = value;

    let href = '#/expense_transactions?start=' + year + '-01-01&end=' + year + '-12-31';
    if (indent >= 8) {
      // Leaf category — filter by spending_category
      href += '&spending_category=' + encodeURIComponent(trimmed);
    } else if (indent >= 4) {
      // Top-level category — filter by top_level_category
      href += '&top_level_category=' + encodeURIComponent(trimmed);
    }
    // indent 0 = TOTAL — no category filter, just date range
    content.href = href;
    return content;
  };
}

function buildColumns(year, granularity) {
  const periods = getPeriodsForGranularity(year, granularity);
  const dollarFmt = createDollarFormatter(0);

  const cols = [
    {
      title: "Category",
      field: "spending_category",
      responsive: 0,
      minWidth: 180,
      formatter: categoryLinkFormatter(year),
    },
  ];

  periods.forEach((p) => {
    cols.push({
      title: periodLabel(p),
      field: 'period_' + p,
      responsive: 0,
      formatter: dollarFmt,
      hozAlign: 'right',
      minWidth: 95,
    });
  });

  if (periods.length > 1) {
    cols.push({
      title: 'Total',
      field: 'period_total',
      responsive: 0,
      formatter: dollarFmt,
      hozAlign: 'right',
      minWidth: 95,
    });
  }

  return cols;
}

function Spending() {
  const { initialYear, initialGranularity } = useLoaderData();
  const [, setSearchParams] = useSearchParams();

  const [year, setYear] = useState(initialYear);
  const [granularity, setGranularity] = useState(initialGranularity);
  const [expenses, setExpenses] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periods, setPeriods] = useState(null);
  // Track what the table is actually displaying so columns and data stay in sync
  const [displayYear, setDisplayYear] = useState(initialYear);
  const [displayGranularity, setDisplayGranularity] = useState(initialGranularity);

  // Resolve periods (one-time)
  useEffect(() => {
    let cancelled = false;
    getPeriods().then((p) => {
      if (!cancelled) {
        setPeriods(p);
        // If initial year isn't available, switch to most recent
        setYear((prev) => (p.includes(prev) ? prev : (p[0] || prev)));
      }
    });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch expenses when year/granularity changes
  useEffect(() => {
    if (!year) return;
    let cancelled = false;
    setLoading(true);
    getExpenses(year, granularity).then((data) => {
      if (!cancelled) {
        setExpenses(data);
        setDisplayYear(year);
        setDisplayGranularity(granularity);
        setLoading(false);
      }
    });
  }, [year, granularity]);

  const handleYearChange = (e) => {
    const y = e.target.value;
    setYear(y);
    const params = { year: y };
    if (granularity !== 'annual') params.granularity = granularity;
    setSearchParams(params, { replace: true });
  };

  const handleGranularityChange = (g) => {
    setGranularity(g);
    const params = { year };
    if (g !== 'annual') params.granularity = g;
    setSearchParams(params, { replace: true });
  };

  if (!periods || expenses === null) {
    return <LoadingScreen visible="true" />;
  }

  return (
    <div className="spending">
      <div className="spending-controls">
        <h2>Spending in {year}</h2>
        <select value={year || ''} onChange={handleYearChange}>
          {periods.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <div className="granularity-toggle">
          {['annual', 'quarterly', 'monthly'].map((g) => (
            <button
              key={g}
              className={granularity === g ? 'active' : ''}
              onClick={() => handleGranularityChange(g)}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className={'spending-table-wrapper' + (loading ? ' loading' : '')}>
      <ReactTabulator
        data={expenses}
        options={{
          dataTree: true,
          dataTreeStartExpanded: [true, false],
          responsiveLayout: "hide",
        }}
        columns={buildColumns(displayYear, displayGranularity)}
      />
      </div>
    </div>
  );
}

export default Spending;
